import type { Escopo } from './escopo';
import { ehDoEscopo } from './escopo';
import { ehGastoDoOrcamento } from './mesFinanceiro';
import type { LinhaDoMes } from './mesFinanceiro';
import type { ChaveDeTipo, MapaDeTipos } from './tiposDeGasto';
import { NAO_CLASSIFICADO, tipoDaCategoria } from './tiposDeGasto';

/** Gasto sem categoria nenhuma. Mesmo id usado por `porCategoria.ts`. */
export const ID_SEM_CATEGORIA_MEDIA = '__sem_categoria__';

export type MediaDeCategoria = {
  categoriaId: string;
  nome: string;
  tipo: ChaveDeTipo;
  /** Gasto médio por mês, em centavos, positivo. */
  media: number;
  /** Soma do gasto na janela inteira, em centavos, positivo. */
  totalNaJanela: number;
};

export type MediaDeTipo = {
  tipo: ChaveDeTipo;
  media: number;
};

export type Medias = {
  /**
   * ⚠️⚠️ Quantos meses REALMENTE tinham movimento — o divisor de verdade.
   * A tela é obrigada a mostrar este número ("média de 2 meses"). Sem ele, uma
   * janela de 6 meses com 1 mês de dado devolveria uma média 6× menor que a
   * realidade, com cara de número certo. O app foi recriado em 07/09/2026: ter
   * pouco histórico é o caso NORMAL aqui, não a exceção.
   *
   * ⚠️⚠️ Fix round 1 (Critical): "mês com dado" é QUALQUER movimento do mês
   * dentro do escopo — receita, transferência, gasto — não só gasto. Um mês
   * em que o dono só recebeu salário e não gastou nada ainda É um mês de uso
   * real do app; contá-lo de fora do divisor infla a média (o mês vira
   * invisível em vez de puxar a média para baixo com um gasto zero). "Mês sem
   * dado" e "mês sem gasto" são coisas diferentes — zero gasto também é dado.
   */
  mesesConsiderados: number;
  /** Ordenado pela média, do maior para o menor. */
  porCategoria: MediaDeCategoria[];
  /** Sempre os quatro baldes, inclusive os zerados. */
  porTipo: MediaDeTipo[];
};

/** A ordem em que a tela mostra os baldes. `NAO_CLASSIFICADO` é sempre o último. */
const BALDES: ChaveDeTipo[] = [
  'essencial',
  'luxo',
  'investimento',
  NAO_CLASSIFICADO,
];

/**
 * Média de gasto por categoria e por tipo, numa janela de meses.
 *
 * ⚠️ `ehGastoDoOrcamento` é a MESMA guarda de `resumirMes` e
 * `agruparPorCategoria` — reusada de propósito, e não reescrita: ela é quem
 * exclui transferência, saldo inicial, linha-pai de split, conta fora do
 * orçamento e escopo errado. Uma segunda definição de "isto conta como gasto?"
 * faria a tela Médias e a tela Mês discordarem sobre o mesmo mês.
 *
 * ⚠️⚠️ Fix round 1 (Critical): "o mês entra no divisor" e "a linha soma
 * gasto" são perguntas DIFERENTES, com guardas diferentes de propósito.
 * `ehGastoDoOrcamento` (usada só para acumular `gastoPorCategoria`) exige
 * despesa — por isso não serve para decidir se um mês existiu. Um mês
 * contar como movimento usa `ehDoEscopo` + as duas guardas que sobram depois
 * de tirar "é despesa" da equação (saldo inicial e linha-pai de split, que
 * são artefato/duplicata, não sinal de uso real). Receita e transferência
 * PASSAM nessa checagem — de propósito: são sinal de que o dono usou o app
 * naquele mês, mesmo sem gastar nada.
 *
 * ⚠️ `escopo` é OBRIGATÓRIO (Global Constraints) — ver `contasFixas.ts`,
 * Fix round 2 Finding 4, onde um default virou silenciador.
 */
export function calcularMedias(
  linhas: LinhaDoMes[],
  meses: string[],
  nomes: Map<string, string>,
  mapa: MapaDeTipos,
  escopo: Escopo,
): Medias {
  const janela = new Set(meses);

  const gastoPorCategoria = new Map<string, number>();
  const mesesComMovimento = new Set<string>();

  for (const l of linhas) {
    const mes = l.date.slice(0, 7);
    if (!janela.has(mes)) continue;

    // Mês conta como "com dado" por QUALQUER movimento do escopo pedido —
    // não só gasto (ver o JSDoc acima). Saldo inicial é artefato de abertura
    // de conta; linha-pai de split duplicaria o que já está nos filhos.
    if (ehDoEscopo(l.notes, escopo) && !l.ehSaldoInicial && !l.ehPai) {
      mesesComMovimento.add(mes);
    }

    if (!ehGastoDoOrcamento(l, escopo)) continue;

    const chave = l.category ?? ID_SEM_CATEGORIA_MEDIA;
    gastoPorCategoria.set(
      chave,
      (gastoPorCategoria.get(chave) ?? 0) + -l.amount,
    );
  }

  const mesesConsiderados = mesesComMovimento.size;
  // Guarda defensiva hoje inalcançável: `divisor` só é usado abaixo dentro do
  // .map() de `gastoPorCategoria`, que não roda se o mapa está vazio — e o
  // mapa só fica vazio quando `mesesConsiderados` é 0 (nenhuma linha passou
  // por `ehGastoDoOrcamento`). Fica escrita porque é barata e correta, não
  // porque algum caminho real a alcança.
  const divisor = mesesConsiderados === 0 ? 1 : mesesConsiderados;

  const porCategoria: MediaDeCategoria[] = [...gastoPorCategoria.entries()].map(
    ([categoriaId, total]) => ({
      categoriaId,
      nome:
        categoriaId === ID_SEM_CATEGORIA_MEDIA
          ? 'Sem categoria'
          : (nomes.get(categoriaId) ?? 'Sem categoria'),
      tipo:
        categoriaId === ID_SEM_CATEGORIA_MEDIA
          ? NAO_CLASSIFICADO
          : tipoDaCategoria(categoriaId, nomes, mapa),
      media: Math.round(total / divisor),
      totalNaJanela: total,
    }),
  );

  porCategoria.sort((a, b) => b.media - a.media);

  /**
   * ⚠️ Os quatro baldes SEMPRE aparecem, inclusive zerados. O balde
   * `NAO_CLASSIFICADO` visível é o que substitui a fila de Pendências, que
   * saiu do menu: categoria faltando envenena a média, e este número é o
   * único aviso que resta.
   *
   * ⚠️ De propósito, soma as médias JÁ ARREDONDADAS de `porCategoria`, em vez
   * de `Math.round(totalDoTipo / divisor)`. As duas contas podem divergir em
   * 1 centavo por arredondamento, mas somar o que está na tela garante que o
   * total do balde sempre bate com a soma das categorias listadas abaixo
   * dele — um total que não fecha com suas parcelas é pior do que um centavo
   * de diferença. Não "corrigir" para a divisão direta.
   */
  const porTipo: MediaDeTipo[] = BALDES.map(tipo => ({
    tipo,
    media: porCategoria
      .filter(c => c.tipo === tipo)
      .reduce((soma, c) => soma + c.media, 0),
  }));

  return { mesesConsiderados, porCategoria, porTipo };
}
